import React, { useState, useCallback, useEffect, useContext, FC } from 'react';
import {
  usePlaidLink,
  PlaidLinkOnExit,
  PlaidLinkOnExitMetadata,
  PlaidLinkError,
  PlaidLinkOnSuccess,
  PlaidLinkOnEvent,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnEventMetadata,
} from 'react-plaid-link';
import styled from 'styled-components';

import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { CreditCardApplicationContext } from '../../CreditCardApplicationContext';
import { ErrorContext } from '../../ErrorContext';
import {
  PLAID_LINK_TOKEN_KEY,
  BANK_BLOCKLIST_OPTIMIZELY_KEY,
  UNAUTHORIZED_MESSAGE,
} from '../constants';
import { createPlaidLinkToken } from '../data/queries';
import { getFeatureVariables } from '../utilities/feature-flags';
import {
  logError,
  heapTrackPlaidEvent,
  heapTrackPlaidExitedLinkFlow,
  nrTrackPlaidEvent,
  nrTrackPlaidExit,
  nrTrackPlaidSuccess,
} from '../utilities/logging';
import { prefixRoute } from '../utilities/routes';

import { ActionButton } from './Button';

// Plaid integration is encapsulated within the components of this file

// PlaidLink
//   - Generates Plaid Link tokens via graphQL mutation createPlaidLinkToken
//   - Conditionally renders PlaidLinkPortal when there is a valid Link token
//   - Persists Link token in session storage to use in case of OAuth bank connection flow
//   - Reinitializes Plaid using query param oauth_state_id after the first OAuth sign-in
//   - Tracks Heap Event 'NPC - Plaid Link Event' from PlaidLink.onEvent callback
//   - Tracks Heap Event 'NPC - Plaid Exited Link Flow' from PlaidLink.onExit callback

// PlaidLinkPortal
//   - Opens Plaid with the usePlaidLink hook

// Types for topmost component encapsulating Plaid, i.e. PlaidLink
interface PlaidModuleProps {
  children: React.ReactNode | React.ReactNodeArray;
  authorized: boolean;
  plaidShouldBeOpen: boolean;
  onConsent: Function;
  consentInProgress: boolean;
  onSuccess: (
    publicToken: string,
    accountId: string,
    institutionName?: string
  ) => void;
  onExit: (institutionName?: string) => void;
  onOpenClick: Function;
  onEvent?: (eventProps: any) => void;
}

// Types for child component PlaidLinkPortal
interface PlaidLinkPortalProps {
  children: React.ReactNode | React.ReactNodeArray;
  token: string;
  plaidShouldBeOpen: boolean;
  onSuccess: PlaidLinkOnSuccess;
  onEvent: PlaidLinkOnEvent;
  onExit: PlaidLinkOnExit;
  onOpenClick: Function;
}

// Button to open Plaid
const OpenPlaidButton = styled(ActionButton)`
  cursor: pointer;
  &:disabled {
    cursor: progress;
  }
`;
OpenPlaidButton.displayName = 'OpenPlaidButton';

// Component encapsulating usePlaidLink hook is rendered only when there is a valid Link token
const PlaidLinkPortal = (props: PlaidLinkPortalProps) => {
  const {
    children,
    token,
    plaidShouldBeOpen,
    onSuccess,
    onEvent,
    onExit,
    onOpenClick,
  } = props;

  // Configure Plaid Link
  const config = {
    token,
    onSuccess,
    onEvent,
    onExit,
    receivedRedirectUri: /oauth_state_id/.test(window.location.search)
      ? window.location.href
      : null,
  };

  // Initialize Plaid
  const { open, ready } = usePlaidLink(config);

  // Automatically open Plaid widget when ready if OAuth redirected the user back here
  useEffect(() => {
    if (
      ready &&
      (plaidShouldBeOpen || /oauth_state_id/.test(window.location.search))
    ) {
      open();
    }
  }, [ready, plaidShouldBeOpen]);

  return (
    <OpenPlaidButton
      id="plaid-link-button"
      type="button"
      onClick={onOpenClick}
      disabled={!ready || plaidShouldBeOpen}
    >
      {children}
    </OpenPlaidButton>
  );
};

// Conditionally renders PlaidLinkPortal after a Link token is obtained
export const PlaidLink: FC<PlaidModuleProps> = ({
  children,
  onSuccess,
  onExit,
  onConsent,
  onOpenClick,
  consentInProgress,
  plaidShouldBeOpen,
  authorized,
  onEvent,
}) => {
  // Use context to get the applicationId for createPlaidLinkToken request
  const { creditCardApplication } = useContext(CreditCardApplicationContext);

  // Show error modal in case of unhandled errors
  const { setShowErrorModal } = useContext(ErrorContext);

  // Link token used to initialize Plaid session is cached and passed to PlaidLinkPortal
  const [linkToken, setLinkToken] = useState('');
  const [fetchingLinkToken, setFetchingLinkToken] = useState(false);

  // Get exit event properties for Heap event NPC - Plaid Exited Link Flow
  const preparePropsOnExit = (
    error: null | PlaidLinkError,
    metadata: PlaidLinkOnExitMetadata
  ) => ({
    errorType: error && error.error_type ? error.error_type : '',
    errorCode: error && error.error_code ? error.error_code : '',
    errorMessage: error && error.error_message ? error.error_message : '',
    errorDisplayMessage:
      error && error.display_message ? error.display_message : '',
    institutionName:
      metadata &&
      metadata.institution &&
      typeof metadata.institution !== 'string' &&
      metadata.institution.name
        ? metadata.institution.name
        : '',
    institutionId:
      metadata &&
      metadata.institution &&
      typeof metadata.institution !== 'string' &&
      metadata.institution.institution_id
        ? metadata.institution.institution_id
        : '',
    status: metadata && metadata.status ? metadata.status : '',
    linkSessionId:
      metadata && metadata.link_session_id ? metadata.link_session_id : '',
    requestId: metadata && metadata.request_id ? metadata.request_id : '',
  });

  // Get event properties for Heap event NPC - Plaid Event
  const preparePropsOnEvent = (
    eventName: string,
    metadata: PlaidLinkOnEventMetadata
  ) => ({
    eventName,
    errorType: metadata && metadata.error_type ? metadata.error_type : '',
    errorCode: metadata && metadata.error_code ? metadata.error_code : '',
    errorMessage:
      metadata && metadata.error_message ? metadata.error_message : '',
    exitStatus: metadata && metadata.exit_status ? metadata.exit_status : '',
    institutionId:
      metadata && metadata.institution_id ? metadata.institution_id : '',
    institutionName:
      metadata && metadata.institution_name ? metadata.institution_name : '',
    institutionSearchQuery:
      metadata && metadata.institution_search_query
        ? metadata.institution_search_query
        : '',
    mfaType: metadata && metadata.mfa_type ? metadata.mfa_type : '',
    widgetViewName: metadata && metadata.view_name ? metadata.view_name : '',
    linkSessionId:
      metadata && metadata.link_session_id ? metadata.link_session_id : '',
    requestId: metadata && metadata.request_id ? metadata.request_id : '',
    timestamp: metadata && metadata.timestamp ? metadata.timestamp : '',
    verificationMethod:
      metadata && metadata.selection ? metadata.selection : '',
  });

  // Callback to handle success event when user completes onboarding flow in Plaid widget
  const handleSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      // Remove Link token from session storage before proceeding with verification
      window.sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
      const accountId = metadata.accounts[0].id;
      const { institution } = metadata;
      const { institution_id: institutionId } = institution;
      const institutionName =
        metadata &&
        institution &&
        typeof institution !== 'string' &&
        institution.name
          ? institution.name
          : '';
      const linkSessionId =
        metadata && metadata.link_session_id ? metadata.link_session_id : '';

      // Track New Relic event 'Plaid Success'
      nrTrackPlaidSuccess(
        {
          publicToken,
          institutionId,
          institutionName,
          linkSessionId,
        },
        creditCardApplication.applicationId
      );

      // Grab the list of blocked banks from Optimizely
      const blockedBanks = getFeatureVariables(BANK_BLOCKLIST_OPTIMIZELY_KEY);
      const isBlockListInstitutionId = blockedBanks
        ? !!blockedBanks[institutionId]
        : false;
      // If the chosen bank isn't blocked, success
      if (!isBlockListInstitutionId) {
        // Pass publicToken and accountId to success handler
        onSuccess(publicToken, accountId, institutionName);
      } else {
        setShowErrorModal(true);
      }
    },
    []
  );

  // Callback to handle Plaid Events within Plaid widget
  // https://plaid.com/docs/link/web/#onevent
  const handleEvent = useCallback(
    (eventName: string, metadata: PlaidLinkOnEventMetadata) => {
      // Parse event properties to pass to logs
      const parsedProps = preparePropsOnEvent(eventName, metadata);
      // Track Heap event 'NPC - Plaid Event'
      heapTrackPlaidEvent(parsedProps);
      // Track New Relic event 'Plaid Event'
      nrTrackPlaidEvent(parsedProps, creditCardApplication.applicationId);
      if (onEvent) onEvent(parsedProps);
    },
    []
  );

  // Callback to handle premature exit or errors within Plaid widget
  // https://plaid.com/docs/link/web/#onexit
  const handleExit = useCallback(
    (error: null | PlaidLinkError, metadata: PlaidLinkOnExitMetadata) => {
      // Prepare event properties to pass to logs
      const parsedProps = preparePropsOnExit(error, metadata);
      // Track Heap event 'NPC - Plaid Exited Link Flow'
      heapTrackPlaidExitedLinkFlow(parsedProps);
      // Track New Relic event 'Plaid Exit'
      nrTrackPlaidExit(parsedProps, creditCardApplication.applicationId);
      // Remove the Link token from session storage
      window.sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
      // Clear component state to trigger a new createPlaidLinkToken request
      setLinkToken('');
      // TODO: REMOVE oauth_state_id from URL ??
      // Get institutionName to provide specific messaging for known Plaid issues
      const institutionName =
        metadata &&
        metadata.institution &&
        typeof metadata.institution !== 'string' &&
        metadata.institution.name
          ? metadata.institution.name
          : '';
      onExit(institutionName);
    },
    []
  );

  // Fetch a Link token using graphQL mutation createPlaidLinkToken
  const fetchLinkToken = async () => {
    const { applicationId } = creditCardApplication;
    // if origin is www.prosper.local set origin to https://www.qa32.circleone.com
    const { origin } = window.location;
    const originUrl =
      origin === 'http://www.prosper.local'
        ? 'https://www.qa32.circleone.com'
        : origin;
    const redirectUri = `${originUrl}${prefixRoute('')}`;
    setFetchingLinkToken(true);
    try {
      // App-resume from Apply page since dynamic OAuth redirectUri are not supported by Plaid
      const response = await createPlaidLinkToken(applicationId, redirectUri);
      const { data, errors } = response;
      if (
        data &&
        data.createCreditCardPlaidLinkToken &&
        data.createCreditCardPlaidLinkToken.plaidLinkToken
      ) {
        // Cache the Link token in component state if the request was successful
        const token = data.createCreditCardPlaidLinkToken.plaidLinkToken;
        setLinkToken(token);
        // Persist the newly generated Link token in session storage to use for reinitialization
        window.sessionStorage.setItem(PLAID_LINK_TOKEN_KEY, token);
      }
      if (errors) {
        setShowErrorModal(true);
      }
    } catch (err: any) {
      setShowErrorModal(true);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'ComponentError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'PlaidLink',
          function: 'fetchLinkToken',
        });
      }
    } finally {
      setFetchingLinkToken(false);
    }
  };

  // Generate a new Link token when needed
  useEffect(() => {
    const abortController = new AbortController();
    if (authorized && !linkToken) {
      // if we have the oauth query parameter
      if (/oauth_state_id/.test(window.location.search)) {
        // get Link token from session storage or Application DB
        // and proceed with reinitialization in OAuth flow
        const token =
          window.sessionStorage.getItem(PLAID_LINK_TOKEN_KEY) ||
          creditCardApplication.plaidLinkToken;
        if (token) setLinkToken(token);
        else {
          // If the original token was not found, remove oauth_state_id from url to restart Plaid
          window.location.assign(window.location.pathname);
        }
      } else {
        // Request a fresh Link token via graphQL mutation createPlaidLinkToken
        fetchLinkToken();
      }
    }
    return () => abortController.abort();
  }, [authorized, linkToken]);

  // Render PlaidLinkPortal only when a valid Link token exists in component state
  if (authorized && linkToken !== '') {
    return (
      <PlaidLinkPortal
        token={linkToken}
        plaidShouldBeOpen={plaidShouldBeOpen}
        onSuccess={(publicToken, metadata) =>
          handleSuccess(publicToken, metadata)
        }
        onEvent={(eventName, metadata) => handleEvent(eventName, metadata)}
        onExit={(error, metadata) => handleExit(error, metadata)}
        onOpenClick={onOpenClick}
      >
        {children}
      </PlaidLinkPortal>
    );
  }

  // Return similar button for handling consent before Plaid
  return (
    <OpenPlaidButton
      id="placeholder-plaid-link-button"
      onClick={() => {
        if (!authorized) {
          onConsent();
        } else {
          fetchLinkToken();
          onOpenClick();
        }
      }}
      disabled={consentInProgress || fetchingLinkToken}
    >
      {children}
    </OpenPlaidButton>
  );
};

PlaidLink.defaultProps = {
  onEvent: () => {},
};
